// ═══════════════════════════════════════════════════════════
// RESERVATIONS PAGE - TXMark
// Archivo: src/pages/Reservations.jsx
// Panel de gestión de reservas + tipos de activos + activos
// ═══════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Badge, Modal, EmptyState } from '../components/ui';
import { useAuth } from '../context/AuthContext';
import api from '../api/client';
import toast from 'react-hot-toast';

// ── Estilos base ──────────────────────────────────────────
const inp = { width: '100%', padding: '9px 12px', borderRadius: '8px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', color: '#fff', fontSize: '13px', outline: 'none', boxSizing: 'border-box', fontFamily: 'var(--font-body)' };
const lbl = { fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', display: 'block', marginBottom: '5px' };
const grid2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' };

function Field({ label, children }) {
  return <div><label style={lbl}>{label}</label>{children}</div>;
}

// ── API helpers ───────────────────────────────────────────
const reservAPI = {
  getTypes: () => api.get('/reservations/asset-types'),
  createType: (d) => api.post('/reservations/asset-types', d),
  updateType: (id, d) => api.put(`/reservations/asset-types/${id}`, d),
  deleteType: (id) => api.delete(`/reservations/asset-types/${id}`),
  getAssets: (typeId) => api.get('/reservations/assets', { params: typeId ? { asset_type_id: typeId } : {} }),
  createAsset: (d) => api.post('/reservations/assets', d),
  updateAsset: (id, d) => api.put(`/reservations/assets/${id}`, d),
  deleteAsset: (id) => api.delete(`/reservations/assets/${id}`),
  getReservations: (params) => api.get('/reservations/', { params }),
  updateStatus: (id, d) => api.put(`/reservations/${id}/status`, d),
  cancelReservation: (id) => api.delete(`/reservations/${id}`),
  approveRefund: (id) => api.post(`/reservations/${id}/approve-refund`),
  manualConfirmPayment: (id, d) => api.post(`/reservations/${id}/payments/manual-confirm`, d),
};

// ── Status badges ─────────────────────────────────────────
const STATUS_MAP = {
  pending_deposit: { label: 'Anticipo pendiente', color: '#f59e0b' },
  confirmed: { label: 'Confirmada', color: '#10b981' },
  pending_balance: { label: 'Saldo pendiente', color: '#3b82f6' },
  completed: { label: 'Completada', color: '#6366f1' },
  cancelled: { label: 'Cancelada', color: '#ef4444' },
  no_show: { label: 'No se presentó', color: '#6b7280' },
  cancellation_requested: { label: '🔴 Solicitud reembolso', color: '#f97316' },
};

// ─────────────────────────────────────────────────────────
// FORM: Tipo de Activo
// ─────────────────────────────────────────────────────────
const EMPTY_TYPE = { name: '', slug: '', icon: '📦', billing_unit: 'daily', checkin_time: '08:00', checkout_time: '18:00', advance_deposit_pct: 50, cancellation_policy: '', required_fields: [], field_labels: {} };

const AVAILABLE_FIELDS = [
  { key: 'origen', label: 'Ciudad/Puerto de origen' },
  { key: 'destino', label: 'Ciudad/Puerto de destino' },
  { key: 'pasajeros', label: 'Número de pasajeros' },
  { key: 'ida_vuelta', label: 'Ida y vuelta' },
  { key: 'tipo_servicio', label: 'Tipo de servicio' },
  { key: 'lugar_recogida', label: 'Lugar de recogida' },
  { key: 'lugar_destino', label: 'Lugar de destino' },
  { key: 'tiempo_servicio', label: 'Duración del servicio' },
  { key: 'embarcacion', label: 'Embarcación preferida' },
  { key: 'puerto_salida', label: 'Puerto de salida' },
  { key: 'detalles', label: 'Detalles adicionales' },
];

function AssetTypeForm({ initial, onSave, onCancel, loading }) {
  const [f, setF] = useState(initial ? { ...EMPTY_TYPE, ...initial } : { ...EMPTY_TYPE });
  const set = k => e => setF(p => ({ ...p, [k]: e.target ? e.target.value : e }));

  const handleSave = () => {
    if (!f.name.trim()) { toast.error('El nombre es requerido'); return; }
    if (!f.slug.trim()) f.slug = f.name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    onSave({ ...f, advance_deposit_pct: parseFloat(f.advance_deposit_pct) });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      <div style={grid2}>
        <Field label="Nombre *"><input style={inp} value={f.name} onChange={e => { setF(p => ({ ...p, name: e.target.value, slug: e.target.value.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '') })); }} placeholder="Aeronave, Yate..." /></Field>
        <Field label="Slug"><input style={inp} value={f.slug} onChange={set('slug')} placeholder="aeronave" /></Field>
      </div>
      <div style={grid2}>
        <Field label="Ícono (emoji)"><input style={inp} value={f.icon} onChange={set('icon')} placeholder="✈️" /></Field>
        <Field label="Unidad de facturación">
          <select style={inp} value={f.billing_unit} onChange={set('billing_unit')}>
            <option value="hourly">Por hora</option>
            <option value="daily">Por día</option>
            <option value="fixed">Precio fijo</option>
          </select>
        </Field>
      </div>
      {f.billing_unit === 'daily' && (
        <div style={grid2}>
          <Field label="Hora check-in"><input style={inp} type="time" value={f.checkin_time} onChange={set('checkin_time')} /></Field>
          <Field label="Hora check-out"><input style={inp} type="time" value={f.checkout_time} onChange={set('checkout_time')} /></Field>
        </div>
      )}
      <Field label={`% anticipo por defecto (${f.advance_deposit_pct}%)`}>
        <input style={inp} type="range" min="10" max="100" step="5" value={f.advance_deposit_pct} onChange={set('advance_deposit_pct')} />
      </Field>
      <Field label="Política de cancelación (texto libre)">
        <textarea style={{ ...inp, minHeight: '80px', resize: 'vertical' }} value={f.cancellation_policy} onChange={set('cancellation_policy')} placeholder="Ej: El anticipo no es reembolsable. Cancelaciones con más de 48h reciben el 50%..." />
      </Field>
      <div style={{ background: 'var(--bg-elevated)', borderRadius: '10px', padding: '14px', border: '1px solid var(--border)' }}>
        <p style={{ ...lbl, marginBottom: '12px', color: 'var(--text-primary)' }}>📋 Campos requeridos al reservar</p>
        <p style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '10px' }}>Selecciona qué información debe entregar el cliente al hacer una reserva de este tipo:</p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {AVAILABLE_FIELDS.map(field => {
            const isSelected = (f.required_fields || []).includes(field.key);
            return (
              <button key={field.key} type="button" onClick={() => {
                const current = f.required_fields || [];
                const updated = isSelected ? current.filter(k => k !== field.key) : [...current, field.key];
                setF(p => ({ ...p, required_fields: updated }));
              }} style={{ padding: '6px 12px', borderRadius: '20px', border: `1px solid ${isSelected ? '#6366f1' : 'var(--border)'}`, background: isSelected ? '#6366f122' : 'transparent', color: isSelected ? '#818cf8' : 'var(--text-muted)', fontSize: '12px', cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
                {isSelected ? '✓ ' : ''}{field.label}
              </button>
            );
          })}
        </div>
      </div>
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
        <Button variant="ghost" onClick={onCancel}>Cancelar</Button>
        <Button onClick={handleSave} loading={loading}>Guardar tipo</Button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// FORM: Activo
// ─────────────────────────────────────────────────────────
const EMPTY_ASSET = { name: '', asset_type_id: '', description: '', capacity: '', base_price: '', currency: 'COP', location: '', features: '', service_options: '', origin_options: '', advance_deposit_pct: '', balance_reminder_hours: 48, balance_reminder_hours_2: 24, auto_cancel_hours: 12, cancellation_retention_pct: 100 };

function AssetForm({ initial, types, onSave, onCancel, loading }) {
  const [f, setF] = useState(initial ? { ...EMPTY_ASSET, ...initial, features: Array.isArray(initial.features) ? initial.features.join(', ') : '', service_options: Array.isArray(initial.service_options) ? initial.service_options.join(', ') : (initial.service_options || ''), origin_options: Array.isArray(initial.origin_options) ? initial.origin_options.join(', ') : (initial.origin_options || '') } : { ...EMPTY_ASSET });
  const set = k => e => setF(p => ({ ...p, [k]: e.target ? e.target.value : e }));

  const selectedType = types.find(t => t.id === f.asset_type_id);

  const handleSave = () => {
    if (!f.name.trim() || !f.asset_type_id || !f.base_price) { toast.error('Nombre, tipo y precio son requeridos'); return; }
    const payload = { ...f, base_price: parseFloat(f.base_price), capacity: f.capacity ? parseInt(f.capacity) : null, advance_deposit_pct: f.advance_deposit_pct !== '' ? parseFloat(f.advance_deposit_pct) : null, features: f.features ? f.features.split(',').map(s => s.trim()).filter(Boolean) : [], service_options: f.service_options ? f.service_options.split(',').map(s => s.trim()).filter(Boolean) : [], origin_options: f.origin_options ? f.origin_options.split(',').map(s => s.trim()).filter(Boolean) : [], balance_reminder_hours: parseInt(f.balance_reminder_hours), balance_reminder_hours_2: parseInt(f.balance_reminder_hours_2), auto_cancel_hours: parseInt(f.auto_cancel_hours), cancellation_retention_pct: parseFloat(f.cancellation_retention_pct) };
    onSave(payload);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      <div style={grid2}>
        <Field label="Nombre *"><input style={inp} value={f.name} onChange={set('name')} placeholder="Ej: Cessna 172, Yate Sirena" /></Field>
        <Field label="Tipo de activo *">
          <select style={inp} value={f.asset_type_id} onChange={set('asset_type_id')}>
            <option value="">Seleccionar...</option>
            {types.map(t => <option key={t.id} value={t.id}>{t.icon} {t.name}</option>)}
          </select>
        </Field>
      </div>
      <div style={grid2}>
        <Field label="Precio base *"><input style={inp} type="number" value={f.base_price} onChange={set('base_price')} placeholder={selectedType ? `por ${selectedType.billing_unit === 'hourly' ? 'hora' : selectedType.billing_unit === 'daily' ? 'día' : 'reserva'}` : '0'} /></Field>
        <Field label="Moneda">
          <select style={inp} value={f.currency} onChange={set('currency')}>
            <option value="COP">COP</option>
            <option value="USD">USD</option>
            <option value="EUR">EUR</option>
          </select>
        </Field>
      </div>
      <div style={grid2}>
        <Field label="Capacidad (personas)"><input style={inp} type="number" value={f.capacity} onChange={set('capacity')} placeholder="Ej: 4" /></Field>
        <Field label="Ubicación / Base"><input style={inp} value={f.location} onChange={set('location')} placeholder="Ej: Aeropuerto El Dorado, Bogotá" /></Field>
      </div>
      <Field label="Descripción"><textarea style={{ ...inp, minHeight: '60px', resize: 'vertical' }} value={f.description} onChange={set('description')} /></Field>
      <Field label="Características (separadas por coma)"><input style={inp} value={f.features} onChange={set('features')} placeholder="WiFi, Catering, Piloto incluido" /></Field>
      <Field label="Opciones de servicio (separadas por coma)"><input style={inp} value={f.service_options} onChange={set('service_options')} placeholder="Ej: Escolta, Traslado, Tour — opciones que el cliente puede elegir" /></Field>
      <Field label="Ciudades/Puertos/Bases de origen (separadas por coma)"><input style={inp} value={f.origin_options} onChange={set('origin_options')} placeholder="Ej: Cali, Bogotá, Medellín, Cartagena" /></Field>

      <div style={{ background: 'var(--bg-elevated)', borderRadius: '10px', padding: '14px', border: '1px solid var(--border)' }}>
        <p style={{ ...lbl, marginBottom: '12px', color: 'var(--text-primary)' }}>⚙️ Configuración de pagos y cancelación</p>
        <div style={grid2}>
          <Field label="% anticipo (dejar vacío = hereda del tipo)"><input style={inp} type="number" min="10" max="100" value={f.advance_deposit_pct} onChange={set('advance_deposit_pct')} placeholder={selectedType ? `${selectedType.advance_deposit_pct}% (hereda)` : 'Ej: 50'} /></Field>
          <Field label="Retención anticipo si cancela (%)"><input style={inp} type="number" min="0" max="100" value={f.cancellation_retention_pct} onChange={set('cancellation_retention_pct')} /></Field>
        </div>
        <div style={{ ...grid2, marginTop: '12px' }}>
          <Field label="1er recordatorio saldo (horas antes)"><input style={inp} type="number" value={f.balance_reminder_hours} onChange={set('balance_reminder_hours')} /></Field>
          <Field label="2do recordatorio saldo (horas antes)"><input style={inp} type="number" value={f.balance_reminder_hours_2} onChange={set('balance_reminder_hours_2')} /></Field>
        </div>
        <div style={{ marginTop: '12px' }}>
          <Field label="Cancelación automática tras vencimiento (horas de gracia)"><input style={{ ...inp, width: '50%' }} type="number" value={f.auto_cancel_hours} onChange={set('auto_cancel_hours')} /></Field>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
        <Button variant="ghost" onClick={onCancel}>Cancelar</Button>
        <Button onClick={handleSave} loading={loading}>Guardar activo</Button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────
export default function ReservationsPage() {
  const { tenant } = useAuth();
  const [tab, setTab] = useState('reservations'); // 'reservations' | 'assets' | 'types'
  const [types, setTypes] = useState([]);
  const [assets, setAssets] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');

  // Modales
  const [typeModal, setTypeModal] = useState(false);
  const [editType, setEditType] = useState(null);
  const [assetModal, setAssetModal] = useState(false);
  const [editAsset, setEditAsset] = useState(null);
  const [reservModal, setReservModal] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [t, a, r] = await Promise.all([
        reservAPI.getTypes(),
        reservAPI.getAssets(),
        reservAPI.getReservations({ status: filterStatus || undefined, asset_type_id: filterType || undefined }),
      ]);
      setTypes(t.data || []);
      setAssets(a.data || []);
      setReservations(r.data || []);
    } catch (e) {
      toast.error('Error cargando datos');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterType]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── Tipos ────────────────────────────────────────────
  const saveType = async (data) => {
    setSaving(true);
    try {
      if (editType?.id) {
        await reservAPI.updateType(editType.id, data);
        toast.success('Tipo actualizado');
      } else {
        await reservAPI.createType(data);
        toast.success('Tipo creado');
      }
      setTypeModal(false);
      setEditType(null);
      loadAll();
    } catch (e) {
      toast.error('Error guardando tipo');
    } finally { setSaving(false); }
  };

  const deleteType = async (id) => {
    if (!window.confirm('¿Eliminar este tipo de activo?')) return;
    try { await reservAPI.deleteType(id); toast.success('Eliminado'); loadAll(); } catch { toast.error('Error'); }
  };

  // ── Activos ──────────────────────────────────────────
  const saveAsset = async (data) => {
    setSaving(true);
    try {
      if (editAsset?.id) {
        await reservAPI.updateAsset(editAsset.id, data);
        toast.success('Activo actualizado');
      } else {
        await reservAPI.createAsset(data);
        toast.success('Activo creado');
      }
      setAssetModal(false);
      setEditAsset(null);
      loadAll();
    } catch (e) {
      toast.error('Error guardando activo');
    } finally { setSaving(false); }
  };

  const deleteAsset = async (id) => {
    if (!window.confirm('¿Eliminar este activo?')) return;
    try { await reservAPI.deleteAsset(id); toast.success('Eliminado'); loadAll(); } catch { toast.error('Error'); }
  };

  // ── Reservas ─────────────────────────────────────────
  const confirmPayment = async (reservId, type) => {
    try {
      await reservAPI.manualConfirmPayment(reservId, { payment_type: type });
      toast.success(`Pago de ${type === 'deposit' ? 'anticipo' : 'saldo'} confirmado`);
      loadAll();
      setReservModal(null);
    } catch { toast.error('Error confirmando pago'); }
  };

  const cancelReservation = async (id) => {
    if (!window.confirm('¿Cancelar esta reserva?')) return;
    try { await reservAPI.cancelReservation(id); toast.success('Reserva cancelada'); loadAll(); setReservModal(null); } catch { toast.error('Error'); }
  };

  const approveRefund = async (id) => {
    if (!window.confirm('¿Confirmas que ya procesaste el reembolso en Wompi? Esto marcará la reserva como cancelada.')) return;
    try { await reservAPI.approveRefund(id); toast.success('Reembolso aprobado y reserva cerrada'); loadAll(); setReservModal(null); } catch { toast.error('Error'); }
  };

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('es-CO', { timeZone: 'America/Bogota', dateStyle: 'short', timeStyle: 'short' }) : '—';
  const fmtMoney = (n, cur = 'COP') => `${cur} ${parseFloat(n || 0).toLocaleString('es-CO')}`;

  const TABS = [
    { id: 'reservations', label: '📋 Reservas' },
    { id: 'assets', label: '🚁 Activos' },
    { id: 'types', label: '⚙️ Tipos de activo' },
  ];

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '22px', fontWeight: '700' }}>🗓 Gestión de Reservas</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--text-muted)', fontSize: '13px' }}>Aeronaves, yates, vehículos y más</p>
        </div>
        {tab === 'types' && <Button onClick={() => { setEditType(null); setTypeModal(true); }}>+ Nuevo tipo</Button>}
        {tab === 'assets' && <Button onClick={() => { setEditAsset(null); setAssetModal(true); }}>+ Nuevo activo</Button>}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '4px', background: 'var(--bg-elevated)', padding: '4px', borderRadius: '10px', border: '1px solid var(--border)', marginBottom: '20px', width: 'fit-content' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{ padding: '8px 16px', borderRadius: '7px', border: 'none', background: tab === t.id ? 'var(--bg-card)' : 'transparent', color: tab === t.id ? '#fff' : 'var(--text-muted)', fontSize: '13px', fontWeight: tab === t.id ? '700' : '500', cursor: 'pointer', fontFamily: 'var(--font-body)', whiteSpace: 'nowrap' }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── TAB: RESERVAS ─────────────────────────────── */}
      {tab === 'reservations' && (
        <div>
          {/* Filtros */}
          <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
            <select style={{ ...inp, width: 'auto' }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              <option value="">Todos los estados</option>
              {Object.entries(STATUS_MAP).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>

          {loading && <p style={{ color: 'var(--text-muted)' }}>Cargando...</p>}

          {!loading && !reservations.length && (
            <EmptyState icon="🗓" title="Sin reservas" description="Las reservas generadas por WhatsApp o el CRM aparecerán aquí." />
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {reservations.map(r => {
              const st = STATUS_MAP[r.status] || { label: r.status, color: '#6b7280' };
              const asset = assets.find(a => a.id === r.asset_id);
              return (
                <Card key={r.id} style={{ cursor: 'pointer' }} onClick={() => setReservModal(r)}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                        <span style={{ fontWeight: '700', fontSize: '14px' }}>{asset?.name || r.asset_id.slice(0, 8)}</span>
                        <span style={{ background: st.color + '22', color: st.color, padding: '2px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: '600' }}>{st.label}</span>
                        <span style={{ color: 'var(--text-muted)', fontSize: '11px' }}>#{r.id.slice(0, 8).toUpperCase()}</span>
                      </div>
                      <p style={{ margin: '0 0 4px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                        👤 {r.client_name || '—'} · 📱 {r.client_phone || '—'}
                      </p>
                      <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-muted)' }}>
                        📅 {fmtDate(r.start_datetime)} → {fmtDate(r.end_datetime)}
                      </p>
                    </div>
                    <div style={{ textAlign: 'right', minWidth: '140px' }}>
                      <p style={{ margin: '0 0 2px', fontWeight: '700', fontSize: '15px' }}>{fmtMoney(r.total_price, r.currency)}</p>
                      <p style={{ margin: 0, fontSize: '11px', color: '#f59e0b' }}>Anticipo: {fmtMoney(r.advance_amount, r.currency)}</p>
                      <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-muted)' }}>Saldo: {fmtMoney(r.balance_amount, r.currency)}</p>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* ── TAB: ACTIVOS ──────────────────────────────── */}
      {tab === 'assets' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '14px' }}>
          {!assets.length && <EmptyState icon="🚁" title="Sin activos" description="Crea el primer activo para empezar a recibir reservas." />}
          {assets.map(a => {
            const type = types.find(t => t.id === a.asset_type_id);
            return (
              <Card key={a.id}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ fontWeight: '700', fontSize: '15px' }}>{type?.icon || '📦'} {a.name}</span>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)', background: 'var(--bg-elevated)', padding: '2px 8px', borderRadius: '20px' }}>{type?.name || '—'}</span>
                </div>
                {a.location && <p style={{ margin: '0 0 4px', fontSize: '12px', color: 'var(--text-muted)' }}>📍 {a.location}</p>}
                {a.capacity && <p style={{ margin: '0 0 4px', fontSize: '12px', color: 'var(--text-muted)' }}>👥 {a.capacity} personas</p>}
                <p style={{ margin: '0 0 12px', fontWeight: '700', fontSize: '14px' }}>{a.currency} {parseFloat(a.base_price).toLocaleString('es-CO')} / {type?.billing_unit === 'hourly' ? 'hora' : type?.billing_unit === 'daily' ? 'día' : 'reserva'}</p>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <Button variant="ghost" style={{ flex: 1, fontSize: '12px' }} onClick={() => { setEditAsset(a); setAssetModal(true); }}>✏️ Editar</Button>
                  <Button variant="ghost" style={{ fontSize: '12px', color: '#ef4444' }} onClick={() => deleteAsset(a.id)}>🗑</Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* ── TAB: TIPOS ────────────────────────────────── */}
      {tab === 'types' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '14px' }}>
          {!types.length && <EmptyState icon="⚙️" title="Sin tipos de activo" description='Crea tipos como "Aeronave", "Yate" o "Vehículo" para organizar tus activos.' />}
          {types.map(t => (
            <Card key={t.id}>
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>{t.icon}</div>
              <p style={{ margin: '0 0 4px', fontWeight: '700', fontSize: '15px' }}>{t.name}</p>
              <p style={{ margin: '0 0 4px', fontSize: '12px', color: 'var(--text-muted)' }}>Slug: {t.slug}</p>
              <p style={{ margin: '0 0 4px', fontSize: '12px', color: 'var(--text-muted)' }}>Facturación: {t.billing_unit === 'hourly' ? 'Por hora' : t.billing_unit === 'daily' ? 'Por día' : 'Precio fijo'}</p>
              <p style={{ margin: '0 0 12px', fontSize: '12px', color: '#f59e0b' }}>Anticipo: {t.advance_deposit_pct}%</p>
              <div style={{ display: 'flex', gap: '6px' }}>
                <Button variant="ghost" style={{ flex: 1, fontSize: '12px' }} onClick={() => { setEditType(t); setTypeModal(true); }}>✏️ Editar</Button>
                <Button variant="ghost" style={{ fontSize: '12px', color: '#ef4444' }} onClick={() => deleteType(t.id)}>🗑</Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* ── MODAL: Tipo de activo ─────────────────────── */}
      <Modal open={typeModal} title={editType ? 'Editar tipo de activo' : 'Nuevo tipo de activo'} onClose={() => { setTypeModal(false); setEditType(null); }} width={560}>
        <AssetTypeForm initial={editType} onSave={saveType} onCancel={() => { setTypeModal(false); setEditType(null); }} loading={saving} />
      </Modal>

      {/* ── MODAL: Activo ─────────────────────────────── */}
      <Modal open={assetModal} title={editAsset ? 'Editar activo' : 'Nuevo activo'} onClose={() => { setAssetModal(false); setEditAsset(null); }} width={560}>
        <AssetForm initial={editAsset} types={types} onSave={saveAsset} onCancel={() => { setAssetModal(false); setEditAsset(null); }} loading={saving} />
      </Modal>

      {/* ── MODAL: Detalle reserva ────────────────────── */}
      {reservModal && (
        <Modal open={!!reservModal} title={`Reserva #${reservModal.id.slice(0, 8).toUpperCase()}`} onClose={() => setReservModal(null)} width={560}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {(() => {
              const r = reservModal;
              const st = STATUS_MAP[r.status] || { label: r.status, color: '#6b7280' };
              const asset = assets.find(a => a.id === r.asset_id);
              return (
                <>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <span style={{ fontWeight: '700', fontSize: '16px' }}>{asset?.name || '—'}</span>
                    <span style={{ background: st.color + '22', color: st.color, padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' }}>{st.label}</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '13px' }}>
                    <div><span style={lbl}>Cliente</span>{r.client_name || '—'}</div>
                    <div><span style={lbl}>Teléfono</span>{r.client_phone || '—'}</div>
                    <div><span style={lbl}>Email</span>{r.client_email || '—'}</div>
                    <div><span style={lbl}>Canal</span>{r.source}</div>
                    <div><span style={lbl}>Inicio</span>{fmtDate(r.start_datetime)}</div>
                    <div><span style={lbl}>Fin</span>{fmtDate(r.end_datetime)}</div>
                    <div><span style={lbl}>Total</span><strong>{fmtMoney(r.total_price, r.currency)}</strong></div>
                    <div><span style={lbl}>Vto. saldo</span>{fmtDate(r.balance_due_datetime)}</div>
                  </div>
                  <div style={{ background: 'var(--bg-elevated)', borderRadius: '10px', padding: '14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ fontSize: '13px' }}>Anticipo ({r.advance_deposit_pct}%)</span>
                      <strong>{fmtMoney(r.advance_amount, r.currency)}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '13px' }}>Saldo restante</span>
                      <strong>{fmtMoney(r.balance_amount, r.currency)}</strong>
                    </div>
                  </div>
                  {r.status === 'cancellation_requested' && r.refund_amount && (
                    <div style={{ background: '#f9731622', borderRadius: '10px', padding: '14px', border: '1px solid #f97316' }}>
                      <p style={{ margin: '0 0 6px', fontSize: '13px', fontWeight: '700', color: '#f97316' }}>🔴 Solicitud de cancelación pendiente</p>
                      <p style={{ margin: 0, fontSize: '13px' }}>Reembolsar al cliente: <strong>{fmtMoney(r.refund_amount, r.currency)}</strong></p>
                      <p style={{ margin: '4px 0 0', fontSize: '11px', color: 'var(--text-muted)' }}>Procesa la devolución directamente en Wompi, luego aprueba aquí.</p>
                    </div>
                  )}
                  {r.notes && <div><span style={lbl}>Notas del cliente</span><p style={{ margin: 0, fontSize: '13px' }}>{r.notes}</p></div>}
                  {r.internal_notes && <div><span style={{ ...lbl, color: '#f59e0b' }}>Notas internas</span><p style={{ margin: 0, fontSize: '12px', color: 'var(--text-muted)' }}>{r.internal_notes}</p></div>}

                  {/* Acciones */}
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', borderTop: '1px solid var(--border)', paddingTop: '14px' }}>
                    {r.status === 'pending_deposit' && (
                      <Button onClick={() => confirmPayment(r.id, 'deposit')} style={{ fontSize: '12px' }}>✅ Confirmar anticipo</Button>
                    )}
                    {(r.status === 'confirmed' || r.status === 'pending_balance') && (
                      <Button onClick={() => confirmPayment(r.id, 'balance')} style={{ fontSize: '12px', background: '#10b981' }}>✅ Confirmar saldo</Button>
                    )}
                    {r.status === 'cancellation_requested' && (
                      <Button onClick={() => approveRefund(r.id)} style={{ fontSize: '12px', background: '#f97316' }}>💸 Aprobar reembolso (ya pagado en Wompi)</Button>
                    )}
                    {!['cancelled', 'completed', 'no_show', 'cancellation_requested'].includes(r.status) && (
                      <Button variant="ghost" onClick={() => cancelReservation(r.id)} style={{ fontSize: '12px', color: '#ef4444' }}>❌ Cancelar reserva</Button>
                    )}
                  </div>
                </>
              );
            })()}
          </div>
        </Modal>
      )}
    </div>
  );
}
